import styled from '@emotion/styled';

import { hoverable } from '../../interaction-states';
import { typographyVariantStyles } from '../../typography-variants';
import type { ChipRoundness, ChipSize } from './types';

const SIZE_CONFIG: Record<
  ChipSize,
  {
    padding: string;
    gap: number;
    iconSize: number;
    radius: number;
    typography: (typeof typographyVariantStyles)[keyof typeof typographyVariantStyles];
  }
> = {
  sm: {
    padding: '3px 8px',
    gap: 6,
    iconSize: 16,
    radius: 8,
    typography: typographyVariantStyles.body4,
  },
  md: {
    padding: '5px 12px',
    gap: 4,
    iconSize: 20,
    radius: 12,
    typography: typographyVariantStyles.body2,
  },
  lg: {
    padding: '7px 12px',
    gap: 4,
    iconSize: 20,
    radius: 12,
    typography: typographyVariantStyles.body2,
  },
};

const TRANSIENT_PROPS = new Set(['$size', '$roundness', '$selected']);

export const StyledChip = styled('div', {
  shouldForwardProp: (prop) => !TRANSIENT_PROPS.has(prop),
})<{ $size: ChipSize; $roundness: ChipRoundness; $selected: boolean }>(({
  theme,
  $size,
  $roundness,
  $selected,
}) => {
  const { padding, gap, radius, typography } = SIZE_CONFIG[$size];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    boxSizing: 'border-box',
    userSelect: 'none',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',

    padding,
    gap,
    borderRadius: $roundness === 'round' ? theme.radius.full : radius,
    backgroundColor: theme.colors.background.overlay,
    border: `1px solid ${theme.colors.border.default}`,
    color: $selected ? theme.colors.brand.text : theme.colors.text.primary,
    ...typography,
    fontWeight: 500,
    whiteSpace: 'nowrap',

    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease',

    '&[role="button"]': {
      cursor: 'pointer',
    },

    '&:hover:not([aria-disabled="true"])': {
      borderColor: theme.colors.border.strong,
    },

    '&:focus-visible': {
      boxShadow: `0px 0px 0px 2px ${theme.colors.border.focus}`,
    },

    '&[aria-disabled="true"]': {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  };
});

export const StyledLabel = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const StyledIconWrapper = styled('span', {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: ChipSize }>(({ $size }) => {
  const { iconSize } = SIZE_CONFIG[$size];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: iconSize,
    height: iconSize,

    '& svg': {
      width: '100%',
      height: '100%',
    },
  };
});

export const StyledDeleteButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: ChipSize }>(({ theme, $size }) => {
  const { iconSize } = SIZE_CONFIG[$size];

  return {
    ...hoverable(theme),
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: iconSize,
    height: iconSize,
    color: 'inherit',
    cursor: 'pointer',
    outline: 'none',
    borderRadius: 2,

    '& svg': {
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
    },

    '&:disabled': {
      cursor: 'default',
    },
  };
});
